// frontend/src/components/ImagingDataReviewModal.js
// Modal for reviewing and editing AI-extracted imaging data before import

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Table,
  Tag,
  Space,
  Button,
  Divider,
  Typography,
  Card,
  Row,
  Col,
  message,
  Collapse,
  Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  SaveOutlined,
  RobotOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import MomentDatePicker from './MomentDatePicker.tsx';
import {
  importReviewedImagingData,
  getImagingCategoryDisplayName,
  getConfidenceDisplay,
  prepareDataForImport,
  getDefaultDocumentName,
  getDefaultDocumentDescription,
} from '../services/imagingUploadService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const ImagingDataReviewModal = ({
  visible,
  patientId,
  extractedData,
  tempFileInfo,
  patientInfo,
  initialStudyDate,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [importing, setImporting] = useState(false);
  const [editedFindings, setEditedFindings] = useState([]);
  const [studyDate, setStudyDate] = useState(null);

  useEffect(() => {
    if (extractedData) {
      // Initialize edited findings
      setEditedFindings(extractedData.findings || []);
      
      // Set initial study date
      const initialDate = initialStudyDate || extractedData.studyDate;
      setStudyDate(initialDate ? moment(initialDate) : moment());

      // Set form values
      form.setFieldsValue({
        studyDate: initialDate ? moment(initialDate) : moment(),
        studyType: extractedData.studyType,
        modality: extractedData.modality,
        bodyRegion: extractedData.bodyRegion,
        measurements: extractedData.measurements,
        interpretation: extractedData.interpretation,
        impression: extractedData.impression,
        recommendations: extractedData.recommendations,
        comparison: extractedData.comparison,
        technique: extractedData.technique,
        contrast: extractedData.contrast,
        documentName: getDefaultDocumentName(extractedData, initialDate),
        documentDescription: getDefaultDocumentDescription(extractedData),
      });
    }
  }, [extractedData, initialStudyDate, form]);

  /**
   * Handle finding value edit
   */
  const handleFindingEdit = (index, newValue) => {
    const updated = [...editedFindings];
    updated[index] = { ...updated[index], value: newValue };
    setEditedFindings(updated);
  };

  /**
   * Handle import confirmation
   */
  const handleImport = async () => {
    try {
      const values = await form.validateFields();
      
      setImporting(true);

      // Prepare reviewed data
      const reviewedData = {
        category: extractedData.category,
        studyType: values.studyType,
        modality: values.modality,
        bodyRegion: values.bodyRegion,
        findings: editedFindings,
        measurements: values.measurements,
        interpretation: values.interpretation,
        impression: values.impression,
        recommendations: values.recommendations,
        comparison: values.comparison,
        technique: values.technique,
        contrast: values.contrast,
      };

      const confirmedStudyDate = values.studyDate.format('YYYY-MM-DD');
      const preparedData = prepareDataForImport(reviewedData, confirmedStudyDate);

      console.log('[ImagingDataReviewModal] Importing data:', {
        category: preparedData.category,
        studyType: preparedData.studyType,
        findingsCount: preparedData.findings?.length || 0,
        studyDate: confirmedStudyDate,
        tempFileInfo: tempFileInfo
      });

      const response = await importReviewedImagingData(
        patientId,
        preparedData,
        confirmedStudyDate,
        tempFileInfo,
        values.documentName,
        values.documentDescription
      );

      if (response.status === 'success') {
        // Check if the study date is outside the current flowsheet date range
        const studyDate = moment(confirmedStudyDate);
        const today = moment();
        const oneYearAgo = moment().subtract(1, 'year');

        if (studyDate.isBefore(oneYearAgo)) {
          message.success({
            content: (
              <div>
                <div>✅ {response.message || 'Imaging data imported successfully!'}</div>
                <div style={{ marginTop: 8, fontSize: '12px', color: '#faad14' }}>
                  ⚠️ Note: Study date ({studyDate.format('YYYY-MM-DD')}) is more than 1 year ago.
                  You may need to adjust the flowsheet date range to see this data.
                </div>
              </div>
            ),
            duration: 10
          });
        } else {
          message.success(response.message || 'Imaging data imported successfully!');
        }
        onSuccess();
      } else {
        throw new Error(response.message || 'Failed to import data');
      }
    } catch (err) {
      console.error('[ImagingDataReviewModal] Error importing data:', err);
      
      if (err.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to import imaging data';
        message.error(errorMessage);
      }
    } finally {
      setImporting(false);
    }
  };

  /**
   * Findings table columns
   */
  const findingsColumns = [
    {
      title: 'Parameter',
      dataIndex: 'displayName',
      key: 'displayName',
      width: '30%',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '40%',
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) => handleFindingEdit(index, e.target.value)}
          placeholder="Enter value"
        />
      ),
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: '15%',
      render: (confidence) => {
        if (!confidence) return '-';
        const display = getConfidenceDisplay(confidence);
        return (
          <Tag color={display.color}>
            {display.text} ({Math.round(confidence * 100)}%)
          </Tag>
        );
      },
    },
    {
      title: 'Mapped',
      dataIndex: 'mapped',
      key: 'mapped',
      width: '15%',
      render: (mapped) => (
        <Tag color={mapped ? 'success' : 'warning'}>
          {mapped ? 'Yes' : 'Manual'}
        </Tag>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <RobotOutlined />
          <span>Review AI-Extracted Imaging Data</span>
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="import"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleImport}
          loading={importing}
        >
          Confirm & Import to Flowsheet
        </Button>,
      ]}
      maskClosable={false}
    >
      <Alert
        message="Review and Edit Extracted Data"
        description="Please review the AI-extracted data below. You can edit any values before importing to the patient's flowsheet."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        {/* Study Information */}
        <Card title="Study Information" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={
                  <Space>
                    <span>Study Date</span>
                    {extractedData?.studyDate && (
                      <Tag color="blue" icon={<RobotOutlined />}>
                        From PDF
                      </Tag>
                    )}
                  </Space>
                }
                name="studyDate"
                rules={[{ required: true, message: 'Study date is required' }]}
                extra={
                  extractedData?.studyDate
                    ? `AI extracted date: ${moment(extractedData.studyDate).format('YYYY-MM-DD')}`
                    : 'Please select the study date'
                }
              >
                <MomentDatePicker
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  placeholder="Select study date"
                  showToday
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Category">
                <Input
                  value={getImagingCategoryDisplayName(extractedData?.category)}
                  disabled
                  addonBefore={<CheckCircleOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Study Type" name="studyType">
                <Input placeholder="e.g., CT Abdomen, MRI Brain" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Modality" name="modality">
                <Input placeholder="e.g., CT, MRI, Ultrasound" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Body Region" name="bodyRegion">
                <Input placeholder="e.g., Abdomen, Brain, Chest" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Contrast Used" name="contrast">
                <Input placeholder="e.g., IV Contrast, None" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Findings Table */}
        {editedFindings.length > 0 && (
          <Card title="Extracted Findings" size="small" style={{ marginBottom: 16 }}>
            <Table
              columns={findingsColumns}
              dataSource={editedFindings}
              rowKey={(record, index) => index}
              pagination={false}
              size="small"
              scroll={{ y: 300 }}
            />
          </Card>
        )}

        {/* Additional Information */}
        <Collapse defaultActiveKey={['1']} style={{ marginBottom: 16 }}>
          <Panel header="Measurements & Findings" key="1">
            <Form.Item label="Measurements" name="measurements">
              <TextArea rows={3} placeholder="Key measurements and dimensions" />
            </Form.Item>
          </Panel>
          <Panel header="Interpretation & Impression" key="2">
            <Form.Item label="Interpretation" name="interpretation">
              <TextArea rows={4} placeholder="Radiologist interpretation" />
            </Form.Item>
            <Form.Item label="Impression" name="impression">
              <TextArea rows={3} placeholder="Clinical impression and summary" />
            </Form.Item>
          </Panel>
          <Panel header="Additional Details" key="3">
            <Form.Item label="Recommendations" name="recommendations">
              <TextArea rows={2} placeholder="Follow-up recommendations" />
            </Form.Item>
            <Form.Item label="Comparison to Prior Studies" name="comparison">
              <TextArea rows={2} placeholder="Comparison with previous imaging" />
            </Form.Item>
            <Form.Item label="Technique/Protocol" name="technique">
              <Input placeholder="Imaging technique used" />
            </Form.Item>
          </Panel>
        </Collapse>

        {/* Document Information */}
        <Card title="Document Information" size="small">
          <Form.Item
            label="Document Name"
            name="documentName"
            rules={[{ required: true, message: 'Document name is required' }]}
          >
            <Input placeholder="Name for the saved PDF document" />
          </Form.Item>
          <Form.Item label="Document Description" name="documentDescription">
            <TextArea rows={2} placeholder="Description for the saved PDF document" />
          </Form.Item>
        </Card>
      </Form>
    </Modal>
  );
};

export default ImagingDataReviewModal;

